import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useMatch } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  Bars3Icon,
  HomeIcon,
  Squares2X2Icon,
  UserCircleIcon,
  ArrowRightStartOnRectangleIcon,
  SparklesIcon,
  CurrencyYenIcon,
  CalendarDaysIcon,
  ClockIcon,
  QueueListIcon,
  WrenchScrewdriverIcon,
  ChartBarSquareIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '@/hooks/useAuth';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { useUser } from '@/hooks/useUser';
import { useHeaderSafe } from '@/hooks/useHeader';
import UserAvatar from '@/components/common/UserAvatar';
import PlanBadge from '@/components/common/PlanBadge';
import NotificationBell from '@/components/layout/NotificationBell';
import ChatLauncherButton from '@/components/chat/ChatLauncherButton';
import { useTutorialProgress } from '@/hooks/useTutorialProgress';
import { TUTORIAL_IDS } from '@/constants/tutorial';
import { PUBLIC_EVENT_ROUTE } from '@/constants/routes';
import { CREATE_EVENT_ACTIONS } from '@/constants/createEventActions';

/** メニューガイドの項目定義 */
const MENU_GUIDE_ITEMS: { icon: typeof Squares2X2Icon; label: string; hint: string; danger?: boolean }[] = [
  { icon: PlusIcon, label: 'イベントを作成', hint: 'AI・URL・ひな形など、作り方を選んですぐ作成' },
  { icon: Squares2X2Icon, label: 'ダッシュボード', hint: 'このページ。イベントの概要を確認' },
  { icon: CalendarDaysIcon, label: 'イベント一覧', hint: 'フィルターで管理・検索' },
  { icon: QueueListIcon, label: 'タイムライン', hint: '時系列でスケジュール確認' },
  { icon: ClockIcon, label: '活動ログ', hint: '過去のイベントを振り返り' },
  { icon: UserCircleIcon, label: 'マイページ', hint: 'プロフィール・設定' },
  { icon: WrenchScrewdriverIcon, label: '便利ツール', hint: '割り勘・持ち物リストなど、すぐ使える単発ツール' },
  { icon: ArrowRightStartOnRectangleIcon, label: 'ログアウト', hint: '共有端末では必ずログアウトを', danger: true },
];

export default function Header() {
  const { isAuthenticated, user, logout } = useAuth();
  const { isAdmin } = useIsAdmin();
  const { displayName, planType, isSubscriptionAvailable, avatarUrl } = useUser();
  const { isHeaderVisible } = useHeaderSafe();
  const { isTutorialCompleted, completeTutorial, welcomeCompleted } = useTutorialProgress();
  const location = useLocation();

  const isDashboard = location.pathname === '/dashboard';
  /**
   * 未ログイン時にヘッダーへ出す登録ボタンの表示可否。
   * - 公開イベントページ: ゲストは登録なしで回答できるため、CTA を出すと
   *   「登録しないと参加できない」という誤解を招く
   * - 認証ページ: すでに登録フローの中にいるので重複
   */
  const isPublicEventPage = !!useMatch(`/${PUBLIC_EVENT_ROUTE}`);
  const showRegisterCta =
    !isAuthenticated && !isPublicEventPage && location.pathname !== '/auth';
  /** 完全一致のみで判定するパス（サブパスに別メニュー項目があるもの） */
  const exactMatchPaths = new Set(['/events']);
  const isCurrentPath = (path: string) =>
    path === '/' || exactMatchPaths.has(path)
      ? location.pathname === path
      : location.pathname === path || location.pathname.startsWith(path + '/');
  const menuLinkClass = (path: string) =>
    `flex items-center space-x-3 px-4 py-2 transition-colors ${
      isCurrentPath(path)
        ? 'text-primary-600 bg-primary-50 data-[focus]:bg-primary-100'
        : 'text-text-700 data-[focus]:bg-text-50'
    }`;
  const menuIconClass = (path: string) =>
    `w-5 h-5 ${isCurrentPath(path) ? 'text-primary-500' : 'text-text-400'}`;
  const [guideOpen, setGuideOpen] = useState(false);
  const [guideDismissed, setGuideDismissed] = useState(false);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [spotlightCenter, setSpotlightCenter] = useState<{ x: number; y: number } | null>(null);

  // ガイドオープン時にハンバーガーの位置を計測
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!guideOpen) {
        setSpotlightCenter(null);
        return;
      }
      const el = hamburgerRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        setSpotlightCenter({
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        });
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [guideOpen]);

  const showMenuGuide =
    isAuthenticated &&
    welcomeCompleted &&
    isDashboard &&
    !guideDismissed &&
    !isTutorialCompleted(TUTORIAL_IDS.MENU_NAVIGATION);

  const handleLogout = async () => {
    try {
      await logout({ useHostedUI: false });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleDismissGuide = (openMenu = false) => {
    setGuideOpen(false);
    setGuideDismissed(true);
    completeTutorial(TUTORIAL_IDS.MENU_NAVIGATION);
    if (openMenu) {
      // Re-render 後に MenuButton をクリックして通常メニューを開く
      setTimeout(() => {
        menuButtonRef.current?.click();
      }, 50);
    }
  };

  return (
    <header
      className={`fixed w-full bg-white/95 backdrop-blur-sm border-b border-text-200 z-50 transition-transform duration-300 ease-in-out ${
        isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* ロゴ */}
          <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
            <img src="/images/logo_03_square_transparent.png" alt="企画苔" className="sm:hidden h-12" />
            <img src="/images/logo_03_wide_transparent.png" alt="企画苔" className="hidden sm:block h-8" />
          </Link>

          {/* 右側: AIアシスタント + 通知ベル + アバター（ログイン中）/ 登録ボタン（未ログイン） + ハンバーガーメニュー */}
          <div className="flex items-center gap-1">
            {isAuthenticated && <ChatLauncherButton />}
            {isAuthenticated && <NotificationBell />}
            {/* ログイン状態がひと目で分かるアバター。タップでマイページへ */}
            {isAuthenticated && (
              <Link
                to="/profile"
                className={`p-1 rounded-full transition-colors ${
                  isCurrentPath('/profile') ? '' : 'hover:bg-text-100'
                }`}
              >
                <span className="sr-only">マイページ</span>
                <UserAvatar
                  displayName={displayName}
                  email={user?.email}
                  avatarUrl={avatarUrl}
                  size="md"
                  className={
                    isCurrentPath('/profile')
                      ? 'ring-2 ring-primary-600 ring-offset-2 ring-offset-white'
                      : ''
                  }
                />
              </Link>
            )}
            {showRegisterCta && (
              <Link
                to="/auth?mode=register"
                className="mr-1 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
              >
                無料で始める
              </Link>
            )}
            <div className="relative">
            {showMenuGuide ? (
              <>
                {/* ガイドモード: カスタムボタン */}
                <button
                  ref={hamburgerRef}
                  type="button"
                  onClick={() => setGuideOpen(true)}
                  className="relative p-2 rounded-lg text-text-600 hover:text-primary-600 hover:bg-text-100 transition-colors cursor-pointer"
                >
                  <span className="sr-only">メニューを開く</span>
                  <Bars3Icon className="w-6 h-6" />
                  {/* パルスドット */}
                  {!guideOpen && (
                    <span className="absolute top-1 right-1 flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary-500" />
                    </span>
                  )}
                </button>

                {/* 吹き出し（メニュー閉じている時のみ） */}
                {!guideOpen && (
                  <div className="absolute right-0 top-full mt-2 pointer-events-none z-40 animate-[slideDown_0.4s_cubic-bezier(0.16,1,0.3,1)]">
                    <div className="relative bg-text-900/90 text-white text-xs px-3 py-2 rounded-lg shadow-lg backdrop-blur-sm whitespace-nowrap animate-[float_3s_ease-in-out_infinite]">
                      ここからページを移動できます
                      <div className="absolute -top-1 right-3 w-2.5 h-2.5 bg-text-900/90 rotate-45" />
                    </div>
                  </div>
                )}

                {/* ガイドオーバーレイ（Portal でヘッダーの stacking context 外に描画） */}
                {guideOpen && createPortal(
                  <div
                    className="fixed inset-0 z-[55] animate-overlay"
                    onClick={() => handleDismissGuide(false)}
                  >
                    {/* スポットライト（ハンバーガー位置に穴を開ける） */}
                    {spotlightCenter && (
                      <>
                        <div
                          className="fixed rounded-full pointer-events-none"
                          style={{
                            left: spotlightCenter.x - 24,
                            top: spotlightCenter.y - 24,
                            width: 48,
                            height: 48,
                            boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
                          }}
                        />
                        {/* ぐるぐる破線リング */}
                        <div
                          className="fixed rounded-full border-2 border-dashed border-white/70 pointer-events-none animate-[spin_4s_linear_infinite]"
                          style={{
                            left: spotlightCenter.x - 28,
                            top: spotlightCenter.y - 28,
                            width: 56,
                            height: 56,
                          }}
                        />
                      </>
                    )}
                    {/* スポットライト計測前のフォールバック背景 */}
                    {!spotlightCenter && (
                      <div className="absolute inset-0 bg-black/45" />
                    )}

                    {/* ページ紹介カード（中央寄せ）。
                        縦の短い端末でも収まるよう max-h を dvh で制限し、
                        ヘッダーとアクションは固定、リスト部分のみスクロールさせる。 */}
                    <div
                      className="absolute inset-x-4 top-[4.5rem] max-w-sm mx-auto animate-[slideDown_0.3s_cubic-bezier(0.16,1,0.3,1)]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[calc(100dvh-6rem)]">
                        {/* ヘッダー */}
                        <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-5 py-3 shrink-0">
                          <p className="text-white text-base font-semibold">ページの紹介</p>
                          <p className="text-white/80 text-xs mt-0.5">右上のメニューからいつでも移動できます</p>
                        </div>

                        {/* ページリスト（収まらない端末ではここだけスクロール） */}
                        <div className="px-4 py-2 space-y-1 overflow-y-auto min-h-0">
                          {MENU_GUIDE_ITEMS.map(({ icon: Icon, label, hint, danger }) => (
                            <div
                              key={label}
                              className={`flex items-center gap-3 px-3 py-1.5 rounded-lg ${danger ? 'bg-danger-50/60' : 'bg-text-50/80'}`}
                            >
                              <div className={`flex items-center justify-center w-7 h-7 rounded-lg shrink-0 ${danger ? 'bg-danger-100/60' : 'bg-primary-100/60'}`}>
                                <Icon className={`w-4 h-4 ${danger ? 'text-danger-600' : 'text-primary-600'}`} />
                              </div>
                              <div className="min-w-0">
                                <p className={`text-sm font-medium ${danger ? 'text-danger-700' : 'text-text-800'}`}>{label}</p>
                                <p className="text-xs text-text-500 leading-tight">{hint}</p>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* アクションボタン */}
                        <div className="px-5 pt-2 pb-3 flex items-center justify-between shrink-0 border-t border-text-100">
                          <button
                            type="button"
                            onClick={() => handleDismissGuide(false)}
                            className="text-sm text-text-400 hover:text-text-600 transition-colors cursor-pointer"
                          >
                            スキップ
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDismissGuide(true)}
                            className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer"
                          >
                            わかった！
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>,
                  document.body,
                )}
              </>
            ) : (
              /* 通常モード: Headless UI Menu */
              <Menu as="div" className="relative">
                <MenuButton ref={menuButtonRef} className="p-2 rounded-lg text-text-600 hover:text-primary-600 hover:bg-text-100 transition-colors">
                  <span className="sr-only">メニューを開く</span>
                  <Bars3Icon className="w-6 h-6" />
                </MenuButton>

                <MenuItems
                  transition
                  anchor={{ to: 'bottom end', gap: 8 }}
                  className="w-56 max-h-[var(--anchor-max-height)] overflow-y-auto origin-top-right bg-white rounded-xl shadow-md border border-text-200 py-2 z-50 transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
                >
                  {isAuthenticated ? (
                    <>
                      {/* ユーザー情報（クリックでマイページへ）。
                          アバターはヘッダー右上に出しているので、ここは名前・メールと
                          契約中のプラン。プラン取得前はバッジを出さない
                          （読み込み中は無料プラン扱いになり、有料の人に誤表示されるため） */}
                      <div className="border-b border-text-100">
                        <MenuItem>
                          <Link
                            to="/profile"
                            className="block px-4 py-3 transition-colors data-[focus]:bg-text-50"
                          >
                            {/* プランバッジは名前と同じ行に流し込む。
                                名前が長くて収まらないときはインラインの折り返しで
                                自動的に次の行へ落ちる（truncate だと overflow:hidden で
                                折り返し判定が効かないため、ここは折り返し表示にする） */}
                            <p className="text-sm font-medium text-text-900 break-words mb-1">
                              {/* 余白は名前側の mr-2 で持つ。バッジに ml を付けると
                                  折り返して次の行に落ちたときも左に余白が残ってしまう */}
                              <span className="mr-2">{displayName || 'ユーザー'}</span>
                              {isSubscriptionAvailable && (
                                <PlanBadge planType={planType} className="align-top" />
                              )}
                            </p>
                            <p className="text-xs text-text-500 truncate">
                              {user?.email}
                            </p>
                          </Link>
                        </MenuItem>
                      </div>

                      {/* イベントを作成。作り方の5通りをそのまま選べるようにする。
                          ダッシュボードの FAB は長押し（タッチのみ）でしか開かないため、
                          PC で5つを選べるのはここだけ。FAB 自体もダッシュボード限定なので、
                          他のページから作る導線もここが担う。
                          プラン上限のチェックはせず作成ページ側に任せる（FAB と同じ） */}
                      <div className="border-b border-text-100 px-3 py-2">
                        <p className="px-1 pb-1.5 text-xs font-medium text-text-500">
                          イベントを作成
                        </p>
                        <div className="grid grid-cols-3 gap-1">
                          {CREATE_EVENT_ACTIONS.map((action) => (
                            <MenuItem key={action.key}>
                              <Link
                                to="/events/create"
                                state={action.state}
                                className="flex flex-col items-center gap-1 rounded-lg px-1 py-2 transition-colors data-[focus]:bg-text-50"
                              >
                                <span
                                  className={`flex items-center justify-center w-8 h-8 rounded-full text-white shrink-0 ${action.iconBg}`}
                                >
                                  <action.icon className="w-4 h-4" />
                                </span>
                                <span className="text-[11px] leading-tight text-text-700">
                                  {action.shortLabel}
                                </span>
                              </Link>
                            </MenuItem>
                          ))}
                        </div>
                      </div>

                      {/* ログイン時メニュー */}
                      <div className="py-1">
                        <MenuItem>
                          <Link to="/dashboard" className={menuLinkClass('/dashboard')}>
                            <Squares2X2Icon className={menuIconClass('/dashboard')} />
                            <span>ダッシュボード</span>
                          </Link>
                        </MenuItem>
                        <MenuItem>
                          <Link to="/events" className={menuLinkClass('/events')}>
                            <CalendarDaysIcon className={menuIconClass('/events')} />
                            <span>イベント一覧</span>
                          </Link>
                        </MenuItem>
                        <MenuItem>
                          <Link to="/events/timeline" className={menuLinkClass('/events/timeline')}>
                            <QueueListIcon className={menuIconClass('/events/timeline')} />
                            <span>タイムライン</span>
                          </Link>
                        </MenuItem>
                        <MenuItem>
                          <Link to="/logs" className={menuLinkClass('/logs')}>
                            <ClockIcon className={menuIconClass('/logs')} />
                            <span>活動ログ</span>
                          </Link>
                        </MenuItem>
                        <MenuItem>
                          <Link to="/profile" className={menuLinkClass('/profile')}>
                            <UserCircleIcon className={menuIconClass('/profile')} />
                            <span>マイページ</span>
                          </Link>
                        </MenuItem>
                        <MenuItem>
                          <Link to="/tools" className={menuLinkClass('/tools')}>
                            <WrenchScrewdriverIcon className={menuIconClass('/tools')} />
                            <span>便利ツール</span>
                          </Link>
                        </MenuItem>
                      </div>

                      {/* 管理者専用（表示はUI上の便宜のみ。権限はサーバー側で強制） */}
                      {isAdmin && (
                        <div className="border-t border-text-100 py-1">
                          <MenuItem>
                            <Link to="/admin" className={menuLinkClass('/admin')}>
                              <ChartBarSquareIcon className={menuIconClass('/admin')} />
                              <span>管理コンソール</span>
                            </Link>
                          </MenuItem>
                        </div>
                      )}

                      {/* ログアウト */}
                      <div className="border-t border-text-100 pt-1">
                        <MenuItem>
                          <button
                            onClick={handleLogout}
                            className="flex items-center space-x-3 px-4 py-2 w-full text-left text-danger-600 data-[focus]:bg-danger-50 transition-colors"
                          >
                            <ArrowRightStartOnRectangleIcon className="w-5 h-5" />
                            <span>ログアウト</span>
                          </button>
                        </MenuItem>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* 未ログイン時メニュー */}
                      <div className="py-1">
                        <MenuItem>
                          <Link to="/" className={menuLinkClass('/')}>
                            <HomeIcon className={menuIconClass('/')} />
                            <span>トップ</span>
                          </Link>
                        </MenuItem>
                        <MenuItem>
                          <Link to="/features" className={menuLinkClass('/features')}>
                            <SparklesIcon className={menuIconClass('/features')} />
                            <span>機能紹介</span>
                          </Link>
                        </MenuItem>
                        <MenuItem>
                          <Link to="/pricing" className={menuLinkClass('/pricing')}>
                            <CurrencyYenIcon className={menuIconClass('/pricing')} />
                            <span>料金</span>
                          </Link>
                        </MenuItem>
                        <MenuItem>
                          <Link to="/tools" className={menuLinkClass('/tools')}>
                            <WrenchScrewdriverIcon className={menuIconClass('/tools')} />
                            <span>便利ツール</span>
                          </Link>
                        </MenuItem>
                      </div>

                      {/* ログイン・登録 */}
                      <div className="border-t border-text-100 pt-2 px-4 pb-2 space-y-2">
                        <MenuItem>
                          <Link
                            to="/auth"
                            className="block w-full text-center px-4 py-2 text-text-700 border border-text-300 rounded-lg data-[focus]:bg-text-50 transition-colors font-medium"
                          >
                            ログイン
                          </Link>
                        </MenuItem>
                        <MenuItem>
                          <Link
                            to="/auth?mode=register"
                            className="block w-full text-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors font-medium"
                          >
                            無料で始める
                          </Link>
                        </MenuItem>
                      </div>
                    </>
                  )}
                </MenuItems>
              </Menu>
            )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
